import fallbackAudioUrl from './audio/fallback.mp3';
import { getSpeechCache, setSpeechCache, touchSpeechCache } from './audio/cache';
const speechUrlPrefix = 'speech:';
import { getLanguageName, LanguageCode } from './languages';

const VITE_API_URL = import.meta.env.VITE_APP_API_URL ?? '';
export const API_URL = VITE_API_URL?.indexOf('8788') > -1 ? VITE_API_URL.replace('localhost', window.location.hostname) : VITE_API_URL;

const FILE_EXT_REGEX = /audio\/(\w+)/;
const LANGUAGE_SCRIPT_REGEX: Partial<Record<LanguageCode, RegExp>> = {
  ar: /[\u0600-\u06FF]/,
  fa: /[\u0600-\u06FF]/,
  ps: /[\u0600-\u06FF]/,
  ur: /[\u0600-\u06FF]/,
  he: /[\u0590-\u05FF]/,
  el: /[\u0370-\u03FF]/,
  ru: /[\u0400-\u04FF]/,
  uk: /[\u0400-\u04FF]/,
  bg: /[\u0400-\u04FF]/,
  sr: /[\u0400-\u04FF]/,
  hy: /[\u0530-\u058F]/,
  ka: /[\u10A0-\u10FF]/,
  ja: /[\u3040-\u30FF\u4E00-\u9FFF]/,
  zh: /[\u4E00-\u9FFF]/,
  ko: /[\uAC00-\uD7AF]/,
  th: /[\u0E00-\u0E7F]/,
  lo: /[\u0E80-\u0EFF]/,
  km: /[\u1780-\u17FF]/,
  hi: /[\u0900-\u097F]/,
  mr: /[\u0900-\u097F]/,
  ne: /[\u0900-\u097F]/,
  bn: /[\u0980-\u09FF]/,
  pa: /[\u0A00-\u0A7F]/,
  gu: /[\u0A80-\u0AFF]/,
  ta: /[\u0B80-\u0BFF]/,
  te: /[\u0C00-\u0C7F]/,
  kn: /[\u0C80-\u0CFF]/,
  ml: /[\u0D00-\u0D7F]/,
  si: /[\u0D80-\u0DFF]/,
};

function getScriptRegex(languageCode?: LanguageCode): RegExp | null {
  if (!languageCode) {
    return null;
  }
  return LANGUAGE_SCRIPT_REGEX[languageCode] ?? null;
}

export function getWords(content: string, code: LanguageCode) {
  const segmenter = new Intl.Segmenter(code, { granularity: 'word' });
  const iterator = segmenter.segment(content)[Symbol.iterator]();
  let words = [];
  for (const segment of iterator) {
    if (segment.isWordLike) {
      words.push(segment.segment.toLowerCase());
    }
  }
  return words;
}

export async function fetchLogs() {
  const result = await fetch(`${API_URL}/event`);
  return result.json();
}

const DIRTYLINGO_ID = 'DirtyLingoID';
const LEGACY_ID = 'StudyBuddyID';

function getId() {
  let id = localStorage.getItem(DIRTYLINGO_ID);
  if (!id) {
    const legacy = localStorage.getItem(LEGACY_ID);
    if (legacy) {
      localStorage.setItem(DIRTYLINGO_ID, legacy);
      localStorage.removeItem(LEGACY_ID);
      id = legacy;
    }
  }
  if (!id) {
    const foundId = id = crypto.randomUUID ? crypto.randomUUID() : String(Math.random());
    localStorage.setItem(DIRTYLINGO_ID, id);
    writeEvent(id, {
      event: ['USER_INFO', {
        browser: navigator.userAgent,
        language: navigator.language
      }]
    });
    getLocation().then((location: LocationData) => {
      writeLog(foundId, ['LOCATION', location]);
    })
  }
  return id;
}

export function getUserId() {
  return getId();
}

interface LocationData {
  ip: string;
  city: string;
  region: string;
  region_code: string;
  country_code: string;
  country_code_iso3: string;
  country_name: string;
  country_capital: string;
  country_tld: string;
  continent_code: string;
  in_eu: boolean;
  postal: string;
  latitude: number;
  longitude: number;
  timezone: string;
  utc_offset: string;
  country_calling_code: string;
  currency: string;
  currency_name: string;
  languages: string;
  asn: string;
  org: string;
}

async function getLocation() {
  try {
    const res = await fetch(`https://ipapi.co/json/`);
    return await res.json();
  } catch (e) {
    writeError('Failed to fetch IP info', e);
  }
}

export function writeLog(...event: any[]) {
  let id = getId();
  console.log(...event);
  return writeEvent(id, { event });
}

export function writeError(...error: any[]) {
  let id = getId();
  console.error(...error);
  const event = error.map(e => (
    e instanceof Error ? { stack: e.stack, name: e.name, message: e.message } : e)
  );
  return writeEvent(id, { error: true, event }).catch(() => {});
}

function writeEvent(id: string, event: { error?: true, event: any[] }) {
  return fetch(`${API_URL}/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ id, ...event, time: (new Date()).toISOString() }),
  });
}

export async function transcribe(audio: Blob, languageCode?: LanguageCode): Promise<{ message: string; error?: boolean }> {
  let id = getId();
  const formData = new FormData();
  const filename = 'audio.' + audio.type.match(FILE_EXT_REGEX)?.[1];

  formData.append('audio', audio, filename);

  console.log('transcribing')

  try {
    const languageName = languageCode ? getLanguageName(languageCode) : null;
    const prompt = languageName ? `The attached audio conversation (for recitation into text) takes place solely in the ${languageName} language.` : null;
    const url = `${API_URL}/transcriptions?language=${languageCode ?? 'en'}&id=${id}` +
      (prompt ? `&prompt=${encodeURIComponent(prompt)}` : '');
    const response = await fetch(url, { method: 'POST', body: formData });
    console.log('transcribing finished')
    const result = await response.json();
    const scriptRegex = getScriptRegex(languageCode);
    if (languageCode && scriptRegex && typeof result.message === 'string' && !scriptRegex.test(result.message)) {
      return { message: result.message, error: !scriptRegex.test(result.message) };
    }
    return result;
  } catch (error) {
    writeError('Failed to transcribe: ', error);
    return { message: 'Failed to transcribe.', error: true };
  }
}

// export async function speak(message: string, languageCode: LanguageCode, times = 0): Promise<string> {
export async function speak(
  message: string,
  options?: { voice?: string; instructions?: string; retry?: boolean },
): Promise<string> {
  const key = `${options?.voice ?? ''}|${options?.instructions ?? ''}|${message}`;
  if (!options?.retry) {
    const cachedEntry = await getSpeechCache(key);
    if (cachedEntry) {
      void touchSpeechCache(key);
      return`${speechUrlPrefix}${key}`;
    }
  }

  const id = getId();
  const request = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ id, message, retry: false, ...options }),
  };
  try {
    const res = await fetch(`${API_URL}/speech`, request);
    const blob = await res.blob();
    await setSpeechCache(key, blob);
    return`${speechUrlPrefix}${key}`;
  } catch (e) {
    writeError('Failed to generate audio: ', e);

    if (import.meta.env.DEV) {
      return fallbackAudioUrl;
    }
    return '';
  }
}

export async function translate(
  input: string,
  languageCode: LanguageCode,
  options?: CompletionOptions
): Promise<string> {
  try {
    console.log('translate')
    const language = getLanguageName(languageCode);
    const message = `Repeat the following back to me but in ${language} (no explanation, only repeated text):\n${input}`;
    const response = await completions([{
      role: 'user',
      content: message,
    }], options);
    console.log('translate finished')
    return response.content;
  } catch (e) {
    writeError('Failed to translate: ', e);
    return 'Failed to translate.';
  }
}

type CompletionMessage = {
  role: 'user' | 'system' | 'assistant';
  content: string;
};

export type Message = {
  role: 'user' | 'system' | 'assistant';
  content: string;
};

const inflightCompletions = new Map<string, Promise<AssistantMessage>>();

type CompletionOptions = {
  retry?: boolean;
};

export async function completions(
  messages: CompletionMessage[],
  options?: CompletionOptions
): Promise<AssistantMessage> {
  const key = `completions:${options?.retry ? 'retry' : 'cached'}:${JSON.stringify(messages)}`;
  const existing = inflightCompletions.get(key);
  if (existing) {
    return existing;
  }
  const task = (async () => {
    const id = getId();
    console.log('completions');
    const res = await fetch(`${API_URL}/completions`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, messages, retry: options?.retry }),
    });
    console.log('completions');
    const { role, content } = await res.json();
    if (!res.ok) {
      throw new Error(content);
    }
    return { role, content };
  })();
  inflightCompletions.set(key, task);
  return task.finally(() => {
    inflightCompletions.delete(key);
  });
}

export type AssistantMessage = Message & {
  role: 'assistant';
};

export async function generate(
  messages: CompletionMessage[],
  options?: CompletionOptions
): Promise<AssistantMessage> {
  try {
    const { role, content } = await completions(messages, options);
    return {
      role,
      content,
    }
  } catch (e) {
    writeError('Failed to generate completion:', e);

    if (import.meta.env.DEV) {
      return {
        role: 'assistant',
        content: 'Hola, soy Buddy, tu tutor de español.',
      };
    }
  
    return {
      role: 'assistant',
      content: 'I am having connection issues!',
    };
  }
}
